import React, { useState } from 'react';
import { EXCEL_FUNCTION_CATEGORIES, EXCEL_FUNCTIONS } from '../data/functionsData';

export default function Functions({ navigate }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [mostUsedOnly, setMostUsedOnly] = useState(false);

  const cleanString = (val) =>
    String(val ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  // Filter functions based on search query and mostUsed toggle
  const query = cleanString(searchQuery);
  const visibleFunctions = EXCEL_FUNCTIONS.filter((item) => {
    const haystack = cleanString(
      [
        item.name,
        item.description,
        item.syntax,
        item.example,
        item.categoryLabel,
        item.categoryDescription,
        item.usageLabel
      ].join(' ')
    );
    const matchesSearch = !query || haystack.includes(query);
    const matchesMostUsed = !mostUsedOnly || item.usage === 'most-common';
    return matchesSearch && matchesMostUsed;
  });

  const handleClear = () => {
    setSearchQuery('');
    setMostUsedOnly(false);
  };

  const handleCategoryJump = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <main id="function-app">
      <section className="page-hero">
        <div className="container">
          <span className="eyebrow">Free Excel function library</span>
          <h1>200 Excel functions explained</h1>
          <p className="lead max-960">
            Browse functions in the same clear format as the Excel Shortcut library. Select any function to open its syntax, formula example, result and practical workplace explanation.
          </p>
        </div>
      </section>

      <section className="function-tools-section">
        <div className="container function-tools-grid">
          <div className="function-action-area">
            <button className="function-action-primary" id="print-functions" type="button" onClick={handlePrint}>
              Print / Save PDF
            </button>
            <button
              className={`function-action-secondary ${mostUsedOnly ? 'active' : ''}`}
              id="show-most-used"
              type="button"
              aria-pressed={mostUsedOnly}
              onClick={() => setMostUsedOnly(!mostUsedOnly)}
            >
              {mostUsedOnly ? 'Show All Functions' : 'Show Most Used'}
            </button>
          </div>
          <nav className="function-category-nav" aria-label="Function categories">
            <h2>Jump to Category</h2>
            <div id="category-links" className="function-category-links">
              {EXCEL_FUNCTION_CATEGORIES.map((category) => {
                const total = EXCEL_FUNCTIONS.filter((item) => item.category === category.id).length;
                return (
                  <a
                    key={category.id}
                    href={`#${category.id}`}
                    onClick={handleCategoryJump(category.id)}
                  >
                    <span>{category.label}</span>
                    <strong>{total}</strong>
                  </a>
                );
              })}
            </div>
          </nav>
        </div>
      </section>

      <section className="function-search-section">
        <div className="container">
          <label className="function-search-box" htmlFor="function-search">
            <span className="function-search-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="27" height="27" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <circle cx="11" cy="11" r="7"></circle>
                <path d="m20 20-4-4"></path>
              </svg>
            </span>
            <input
              id="function-search"
              type="search"
              placeholder="Search Functions..."
              autoComplete="off"
              aria-label="Filter Excel functions"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </label>
          <div className="function-filter-note">
            Showing <strong>{visibleFunctions.length}</strong> functions
            {(searchQuery || mostUsedOnly) && (
              <button type="button" id="clear-function-filter" onClick={handleClear}>
                Clear filter
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="function-list-section">
        <div className="container">
          <div id="function-results">
            {EXCEL_FUNCTION_CATEGORIES.map((category) => {
              const items = visibleFunctions.filter((item) => item.category === category.id);
              if (items.length === 0) return null;

              return (
                <section key={category.id} className="function-group" id={category.id}>
                  <div className="function-group-heading">
                    <div className="function-category-symbol">{category.icon}</div>
                    <div>
                      <h2>{category.label}</h2>
                      <p>{category.description}</p>
                    </div>
                    <span>
                      {items.length} function{items.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="function-table" role="table" aria-label={category.label}>
                    <div className="function-table-head" role="row">
                      <span role="columnheader">Function</span>
                      <span role="columnheader">What it does</span>
                      <span role="columnheader">Formula example</span>
                    </div>
                    {items.map((item) => (
                      <a
                        key={item.slug}
                        className="function-row"
                        role="row"
                        href={`#/functions/${item.slug}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`functions/${item.slug}`);
                          window.scrollTo({ top: 0 });
                        }}
                      >
                        <span className="function-name-cell" role="cell">
                          <code>={item.name}</code>
                          <small>{item.usageLabel}</small>
                        </span>
                        <span className="function-description-cell" role="cell">
                          {item.description}
                        </span>
                        <span className="function-example-cell" role="cell">
                          <code>{item.example}</code>
                          <small>{item.result}</small>
                        </span>
                      </a>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>

          {visibleFunctions.length === 0 && (
            <div className="function-empty" id="function-empty">
              <h2>No functions found</h2>
              <p>Try a different function name, category, task or formula keyword.</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
